import { useEffect, useState } from "react";
import {
  Box,
  Grid2 as Grid,
  Switch,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useAtom } from "jotai";
import { Button, Modal } from "../../common";
import {
  createOrganizationWizardStepAtom,
  organizationWizardWashTypesAtom,
  selectedOrgIdAtom,
  toastOptionsAtom,
} from "../../../atoms";
import {
  useCreateWashTypeMutation,
  useFetchDefaultWashTypesQuery,
} from "../../../api";
import { WashType } from "../../../types";
import { ChargeType } from "../../../enums";
import { WashTypeForm } from "../../wash-types/fragments/WashTypeForm";

export const OrganizationWashTypesStep = () => {
  const [, setStep] = useAtom(createOrganizationWizardStepAtom);
  const [washTypes, setWashTypes] = useAtom(organizationWizardWashTypesAtom);
  const [selectedOrgId] = useAtom(selectedOrgIdAtom);
  const [, setToastOptions] = useAtom(toastOptionsAtom);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const fetchDefaultWashTypesQuery = useFetchDefaultWashTypesQuery();
  const createWashTypeMutation = useCreateWashTypeMutation();
  useEffect(() => {
    if (fetchDefaultWashTypesQuery.isSuccess && washTypes.length === 0) {
      const defaultWashTypes = fetchDefaultWashTypesQuery.data.map(
        (washType) => ({
          ...washType,
          enabled: true,
          isCreated: false,
        }),
      );

      setWashTypes(defaultWashTypes);
    }
  }, [
    fetchDefaultWashTypesQuery.isSuccess,
    fetchDefaultWashTypesQuery.data,
    washTypes.length,
  ]);

  const enabledCount = washTypes.filter((wt) => wt.enabled).length;

  const handleToggleWashType = (code: string) => {
    setWashTypes((prev) =>
      prev.map((wt) =>
        wt.code === code ? { ...wt, enabled: !wt.enabled } : wt,
      ),
    );
  };

  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
  };

  const handleCreateWashType = (
    data: Omit<WashType, "id" | "miscellaneous">,
  ) => {
    createWashTypeMutation.mutate(
      {
        name: data.name,
        description: data.description,
        unitOfMeasurement: data.unitOfMeasurement,
        code: data.code,
        orgId: data.orgId,
        rate: data.rate,
        miscellaneous: false,
        quickbookReferenceId:
          data.quickbookReferenceId?.trim() === ""
            ? undefined
            : data.quickbookReferenceId,
        chargeType: ChargeType.Wash,
      },
      {
        onSuccess: () => {
          setIsAddModalOpen(false);

          setToastOptions({
            open: true,
            message: "Wash type created",
            severity: "success",
          });

          setWashTypes((prev) => [
            ...prev,
            {
              code: data.code,
              name: data.name,
              unitOfMeasurement: data.unitOfMeasurement || "quantity",
              enabled: true,
              isCreated: true,
            },
          ]);
        },
      },
    );
  };

  const handleNext = () => {
    setStep(2);
  };

  const handleBack = () => {
    setStep(0);
  };

  if (fetchDefaultWashTypesQuery.isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        Wash Types
      </Typography>

      <Typography
        sx={{
          color: "#a6a6a6",
          fontSize: "14px",
          mb: 3,
        }}
      >
        Select the wash types you want to enable for this organization.
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "flex-start", sm: "flex-end" },
          mb: 2,
        }}
      >
        <Button
          label="Add Wash Type"
          variantType="secondary"
          onClick={handleOpenAddModal}
        />
      </Box>

      <Grid container spacing={2}>
        {washTypes.map((washType) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={washType.code}>
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                  {washType.name}
                </Typography>

                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#a6a6a6",
                    mt: 0.5,
                  }}
                >
                  {washType.code}
                </Typography>
              </Box>

              <Switch
                checked={washType.enabled}
                onChange={() => handleToggleWashType(washType.code)}
              />
            </Box>
          </Grid>
        ))}
      </Grid>

      <Typography
        sx={{
          color: "#a6a6a6",
          fontSize: "13px",
          mt: 2,
        }}
      >
        {enabledCount} wash type{enabledCount !== 1 ? "s" : ""} selected
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          gap: 2,
          mt: 4,
        }}
      >
        <Button
          label="Back"
          variantType="secondary"
          onClick={handleBack}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        />

        <Button
          label="Next"
          variantType="primary"
          onClick={handleNext}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        />
      </Box>

      <Modal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add Wash Type"
        content={
          <WashTypeForm
            id="createOrganizationWashTypeForm"
            isCreateMode={true}
            handleEdit={handleCreateWashType}
            defaultValues={
              { orgId: selectedOrgId } as Omit<WashType, "miscellaneous">
            }
          />
        }
        form="createOrganizationWashTypeForm"
        primaryButtonText="Save"
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={() => setIsAddModalOpen(false)}
        sx={{
          modal: {
            width: { lg: "30%", xs: "80%", sm: "50%" },
          },
        }}
      />
    </Box>
  );
};
